'use client'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useRouter } from 'next/router'
import React, { useState } from 'react'

import { isNewDiagnosisExtendedData } from '@/legacy-src/components/diagnosis/client/utils'
import useCompanyBenefits from '@/legacy-src/hooks/useCompanyBenefits'
import useDiagnosis from '@/legacy-src/hooks/useDiagnosis'
import useTherapistCategories from '@/legacy-src/hooks/useTherapistCategories'

import { isRoutePublicTherapistList, PUBLIC_THERAPISTS_PAGE_SLUG } from '../constants/locale.constants'
import useFreeSession from '../hooks/useFreeSession'
import useServices from '../hooks/useServices'
import useSpecializations from '../hooks/useSpecializations'
import type { SpecializationOptionsType, TherapistType } from '../types/TherapistStateType'
import TherapistCardContent from './TherapistCardContent'

type MinifiedTherapistProfilePropsType = TherapistType & {
  readonly specializations?: SpecializationOptionsType
  readonly hideBookButton?: boolean
  readonly hideLinks?: boolean
  readonly isPlaceholder?: boolean
  readonly className?: string
  readonly openModalFromOutside?: (videoId: string) => void
  readonly recommendedAsNthTherapist?: number
  readonly therapist: TherapistType
}

const TherapistCard: React.FC<MinifiedTherapistProfilePropsType> = ({
  recommendations,
  prices,
  therapistId,
  videoId,
  slug,
  price,
  isPlaceholder,
  openModalFromOutside = null,
  recommendedAsNthTherapist,
  therapist,
}) => {
  const router = useRouter()
  const { allowBookingForFree: allowFreeSessions } = useFreeSession()
  const [videoVisible, changeVideoVisible] = useState(false)
  const { selectedServiceId } = useServices()
  const { specializations } = useSpecializations()
  const { diagnosisTest } = useDiagnosis()
  const { shouldHidePriceForService } = useCompanyBenefits()
  const { categories } = useTherapistCategories()
  const shouldUseTherapistCategories = useFeatureIsOn('therapist-categories')
  const displayLanguages = useFeatureIsOn('display-therapist-languages')

  const allowBookingForFree = allowFreeSessions
  const isPublic = isRoutePublicTherapistList(router.route)
  const showTherapeuticScale = Boolean(!isPublic && isNewDiagnosisExtendedData(diagnosisTest))

  const href = isPlaceholder
    ? ''
    : {
        pathname: isPublic ? `/${PUBLIC_THERAPISTS_PAGE_SLUG}/${slug}` : `/client/${therapistId}/profile`,
        query: {
          ...router.query,
          ...(recommendedAsNthTherapist ? { rt: recommendedAsNthTherapist } : {}),
        },
      }

  const handleShowVideo = () => {
    if (openModalFromOutside) {
      openModalFromOutside(videoId)
    } else {
      changeVideoVisible(true)
    }
  }

  const handleCloseVideo = () => {
    changeVideoVisible(false)
  }

  const foundPrice = (prices || []).find((price) => price.serviceId === (selectedServiceId || 1))?.price || 0
  const displayedPrice = price || foundPrice
  const displayRecommendations = Boolean(recommendations)

  return (
    <TherapistCardContent
      prefetch={!isPublic}
      displayedPrice={shouldHidePriceForService(selectedServiceId) ? 0 : displayedPrice}
      displayLanguages={displayLanguages}
      categories={shouldUseTherapistCategories ? categories : undefined}
      {...{
        handleShowVideo,
        videoVisible,
        handleCloseVideo,
        href,
        showTherapeuticScale,
        allowBookingForFree,
        displayRecommendations,
        specializations,
        therapistId,
        openModalFromOutside,
        therapist,
      }}
    />
  )
}

export default TherapistCard
